import React, {
	useCallback,
	useMemo,
	useState,
} from 'react';
import { faRoute } from '@fortawesome/pro-duotone-svg-icons';
import {
	Grid,
	Tag,
} from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import type { SortOrder } from 'antd/lib/table/interface';
import { capitalize } from '@shared/utils/string';
import {
	FixtureCounterpartyTypes,
	FixtureStates,
	FixtureTypes,
	VesselOwnershipTypes,
	Currencies,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import {
	fixtureCounterpartyTypeToName,
	fixtureTypeToName,
	fixtureTypeToNameLower,
} from '@shared/utils/fixtureUtils';
import { standardSort } from '@shared/utils/standardSort';
import type { GetFixturesResponse } from '@api/features/fixtures/getFixtures';
import type { UserData } from '@api/utils/sequelize/calculateUserData';
import VesselDescription from '@client/components/VesselDescription';
import { Links } from '@client/utils/links';
import ListTableScreen from '@client/components/screens/ListTableScreen';
import { getSelectOptions } from '@client/utils/form';
import TooltipIcon from '@client/components/TooltipIcon';
import { getFixtureStateColor } from '@client/utils/colors';
import { getFilterProps } from '@client/utils/table';
import {
	createCounterparty,
	createFixture,
	createVessel,
	getCounterparties,
	getFixtures,
	getVessels,
	updateVessel,
} from '@client/lib/api';
import useFetchedState from '@client/utils/hooks/useFetchedState';
import { useAuth, UserIndexMap } from '@client/lib/auth';
import { vesselIdentifierAndNameSorter } from '@client/screens/fleet/VoyageDetailsScreen/helpers/vesselIdentifierAndNameSorter';
import getVesselOptions from '@client/utils/getVesselOptions';
import { buildUserItemColumn } from '@client/utils/buildUserItem';
import { getUserIdsForKey } from '@client/utils/getUserIdsForKey';
import styles from './styles/FixtureListSceen.module.css';

type Fixture = GetFixturesResponse[number];
type ListFixtureType = Exclude<Values<typeof FixtureTypes>, typeof FixtureTypes.SPOT>;

type NewFixtureValues = {
	vesselId: number | 'new' | null;
	counterpartyId: number | 'new' | null;
	identifier: string;
	vesselName: string;
	vesselCodename: string;
	chartererName: string;
	ownerName: string;
	currency: Values<typeof Currencies>;
};

const getSharedColumns = ({
	fixtures,
	counterpartyType,
	userInfo,
	fixtureType,
	userIndex,
}: {
	fixtures: GetFixturesResponse;
	counterpartyType: Values<typeof FixtureCounterpartyTypes>;
	userInfo: UserData | { filterVessels: boolean; relevantVessels: Array<{ id: number; name: string }> };
	fixtureType: ListFixtureType;
	userIndex: UserIndexMap;
}) => {
	return [
		{
			title: 'Status',
			dataIndex: 'state',
			key: 'state',
			align: 'center' as const,
			render: (state: Values<typeof FixtureStates>) => (
				<Tag color={getFixtureStateColor(state)}>
					{capitalize(state)}
				</Tag>
			),
			width: 150,
		},
		{
			title: 'Vessel',
			dataIndex: 'vesselName',
			key: 'vesselName',
			sorter: (a: Fixture, b: Fixture, type?: SortOrder) => vesselIdentifierAndNameSorter(a, b, 'vesselName', type ?? ''),
			render: (_: unknown, fixture: Fixture) => (
				<VesselDescription
					name={fixture.vesselName ?? '—'}
					flag={fixture.vesselFlag}
				/>
			),
			filteredValue: userInfo.filterVessels ? userInfo.relevantVessels.map((v) => v.name) : [],
			...getFilterProps(fixtures, 'select', 'vesselName', 'desc'),
		},
		{
			title: 'Identifier',
			dataIndex: 'identifier',
			render: (identifier: string | null) => (identifier == null ? (
				<>
					—
					<TooltipIcon>
						The unique identifier is set when a
						vessel is selected for the estimate
					</TooltipIcon>
				</>
			) : identifier),
			key: 'identifier',
			sorter: (a: Fixture, b: Fixture, type?: SortOrder) => vesselIdentifierAndNameSorter(a, b, 'identifier', type ?? ''),
		},
		{
			title: fixtureCounterpartyTypeToName(counterpartyType, fixtureType),
			dataIndex: 'counterpartyName',
			key: 'counterpartyName',
			sorter: standardSort('counterpartyName'),
			...getFilterProps(fixtures, 'select', 'counterpartyName', 'desc'),
		},
		...[
			buildUserItemColumn('fixedBy', 'Fixed By', userIndex, getUserIdsForKey(fixtures, 'fixedBy')),
			buildUserItemColumn('operatedBy', 'Operated By', userIndex, getUserIdsForKey(fixtures, 'operatedBy')),
		],
	];
};

type FixtureListScreenProps = {
	fixtureType: ListFixtureType;
	counterpartyType: Values<typeof FixtureCounterpartyTypes>;
	getExtraColumns: (params: { fixtures: GetFixturesResponse }) => ColumnsType<Fixture>;
	vesselOwnershipType?: Values<typeof VesselOwnershipTypes>;
	allowCreateNewVessel?: boolean;
	allowCreateNewCharterer?: boolean;
	allowCreateNewOwner?: boolean;
};

const FixtureListScreen = ({
	fixtureType,
	counterpartyType,
	getExtraColumns,
	vesselOwnershipType = undefined,
	allowCreateNewVessel = false,
	allowCreateNewCharterer = false,
	allowCreateNewOwner = false,
}: FixtureListScreenProps) => {
	const [creatingNewVessel, setCreatingNewVessel] = useState(false);
	const [creatingNewCharterer, setCreatingNewCharterer] = useState(false);
	const [creatingNewOwner, setCreatingNewOwner] = useState(false);
	const { userInfo, userIndex } = useAuth();
	const screens = Grid.useBreakpoint();

	const [
		fixtures,
		_refreshFixtures,
		fixturesError,
		fixturesLoading,
	] = useFetchedState(() => getFixtures(fixtureType));

	const [
		counterparties,
		_refreshCounterparties,
		counterpartiesError,
		counterpartiesLoading,
	] = useFetchedState(() => getCounterparties(counterpartyType));

	const [
		allVessels,
		_refreshVessels,
		vesselsError,
		vesselsLoading,
	] = useFetchedState(() => getVessels());

	const sortedCounterparties = useMemo(() => (
		(counterparties ?? []).sort((a, b) => a.name.toString().localeCompare(b.name))
	), [counterparties]);

	const availableVessels = useMemo(() => (
		(allVessels ?? [])
			.sort((a, b) => a.name.toString().localeCompare(b.name))
			.filter((v) => (
				vesselOwnershipType === undefined ||
				v.ownershipType === vesselOwnershipType
			))
	), [allVessels, vesselOwnershipType]);

	const createNewFixture = useCallback(async (values: NewFixtureValues) => {
		let {
			vesselId,
			counterpartyId,
		} = values;

		const {
			identifier,
			vesselName,
			vesselCodename,
			chartererName,
			ownerName,
			currency,
		} = values;

		if (vesselId === 'new' && vesselOwnershipType !== undefined) {
			const vessel = await createVessel(vesselName, vesselOwnershipType);

			vesselId = vessel.id;

			await updateVessel(vesselId, { codename: vesselCodename });
		}

		if (counterpartyId === 'new') {
			if (allowCreateNewCharterer) {
				const charterer = await createCounterparty(
					FixtureCounterpartyTypes.CHARTEREROWNER,
					chartererName,
				);

				counterpartyId = charterer.id;
			}

			if (allowCreateNewOwner) {
				const owner = await createCounterparty(
					FixtureCounterpartyTypes.CHARTEREROWNER,
					ownerName,
				);

				counterpartyId = owner.id;
			}
		}

		const fixture = await createFixture(
			vesselId as number,
			counterpartyId as number | undefined,
			identifier,
			fixtureType,
			currency,
		);

		return fixture;
	}, [allowCreateNewCharterer, allowCreateNewOwner, fixtureType, vesselOwnershipType]);

	const columns = useMemo(
		() => {
			if (fixtures === undefined) {
				return [];
			}

			return [
				...getSharedColumns({
					fixtures,
					counterpartyType,
					userInfo,
					fixtureType,
					userIndex,
				}),
				...getExtraColumns({ fixtures }),
			];
		},
		[fixtures, counterpartyType, userInfo, fixtureType, userIndex, getExtraColumns],
	);

	const formFields = useMemo(() => [
		{
			label: 'Vessel',
			name: 'vesselId' as const,
			type: 'select' as const,
			options: [
				{ value: null, label: 'No vessel' },
				...(allowCreateNewVessel ? [{
					label: (<i>Create new...</i>),
					value: 'new',
				}] : []),
				{
					label: 'My vessels',
					options: getVesselOptions(availableVessels, 'myVessels'),
				},
				{
					label: 'Market vessels',
					options: getVesselOptions(availableVessels, 'marketVessels'),
				},
			],
			inputProps: {
				allowClear: true,
				showSearch: true,
			},
			onChange: (vesselId: string | number | boolean | null) => setCreatingNewVessel(vesselId === 'new'),
			required: false,
		},
		{
			label: 'Vessel Name',
			name: 'vesselName' as const,
			type: 'text' as const,
			required: true,
			hide: !creatingNewVessel,
		},
		{
			label: 'Vessel Codename',
			name: 'vesselCodename' as const,
			type: 'text' as const,
			required: true,
			hide: !creatingNewVessel,
		},
		{
			label: fixtureCounterpartyTypeToName(counterpartyType, fixtureType),
			name: 'counterpartyId' as const,
			type: 'select' as const,
			options: [
				...((allowCreateNewCharterer || allowCreateNewOwner) ? [{
					label: (<i>Create new...</i>),
					value: 'new',
				}] : []),
				...getSelectOptions(sortedCounterparties ?? [], 'name', 'id'),
			],
			required: true,
			onChange: (counterpartyId: string | number | boolean | null) => {
				if (allowCreateNewCharterer) {
					setCreatingNewCharterer(counterpartyId === 'new');
				}

				if (allowCreateNewOwner) {
					setCreatingNewOwner(counterpartyId === 'new');
				}
			},
			inputProps: {
				showSearch: true,
			},
		},
		{
			label: 'Charterer Name',
			name: 'chartererName' as const,
			type: 'text' as const,
			required: true,
			hide: !creatingNewCharterer,
		},
		{
			label: 'Owner Name',
			name: 'ownerName' as const,
			type: 'text' as const,
			required: true,
			hide: !creatingNewOwner,
		},
	], [
		allowCreateNewVessel, availableVessels,
		creatingNewVessel, counterpartyType,
		fixtureType, allowCreateNewCharterer,
		allowCreateNewOwner, sortedCounterparties,
		creatingNewCharterer, creatingNewOwner,
	]);

	const loading = fixturesLoading || counterpartiesLoading || vesselsLoading;
	const error = fixturesError || counterpartiesError || vesselsError;

	return (
		<ListTableScreen<Fixture, NewFixtureValues>
			title={`${fixtureTypeToName(fixtureType)} Estimates`}
			rootPageTitle="Estimate - TC"
			columns={columns}
			loading={loading}
			error={error}
			data={fixtures}
			formFields={formFields}
			tableProps={{ useCards: screens.xs }}
			formButtonText={`Create ${fixtureTypeToNameLower(fixtureType)} estimate`}
			formProps={{
				onOpenChange: () => setCreatingNewVessel(false),
				className: styles.createForm,
			}}
			onFormSubmit={createNewFixture}
			getRowHref={(row) => Links.Fixtures[fixtureType].Details.get(row.id)}
			tabs={[{
				tab: 'Active',
				key: 'active',
				filter: (v) => v.state !== FixtureStates.COMPLETED,
				emptyTabTitle: 'You do not have any estimates yet',
				emptyTabDescription: `Click the button to create a ${fixtureTypeToNameLower(fixtureType)} estimate`,
				showEmptyTabArrow: true,
			},
			{
				tab: 'Completed',
				key: 'completed',
				filter: (v) => v.state === FixtureStates.COMPLETED,
				emptyTabTitle: 'You do not have any completed estimates yet',
				emptyTabDescription: 'Once an estimate is complete, it will appear here',
				showEmptyTabArrow: false,
			}]}
			useTabsEmptyText
			emptyTitle="You do not have any estimates yet"
			emptyDescription={
				`Click the button to create a ${fixtureTypeToNameLower(fixtureType)} estime`
			}
			emptyIcon={faRoute}
		/>
	);
};

export default FixtureListScreen;
